import React, { useEffect, useState, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { toast } from 'react-toastify'
import AppSidebar from './AppSidebar'
import InactivityWarningModal from './rr/InactivityWarningModal'
import socketService from '@/services/socketService'
import { AuthService } from '@/services/authService'
import { rrPunch, rrGetAttendanceToday, rrEnsurePunchedIn, rrHeartbeat } from '@/actions/rr'
import { getCurrentUser } from '@/utils/permissions'

const INACTIVITY_MS = 8 * 60 * 1000   // 8 min idle before showing modal
const INACTIVITY_COUNTDOWN_SECONDS = 120  // 2 min countdown in the warning modal
const SALES_REP_ROLE_ID = 9407        // inactivity auto-logout applies to Sales Representatives only
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']
const HEARTBEAT_MS = 60 * 1000        // presence ping every 60s while the app is open

// Punch out if currently punched in, then full logout + redirect
async function punchOutAndLogout(reason: "manual" | "inactivity" = "manual") {
  try {
    const res = await rrGetAttendanceToday()
    const td = res?.data as { status?: string; punch_out?: string | null } | null
    const punchedIn = td?.status === 'present' && !td?.punch_out
    if (punchedIn) await rrPunch(reason)
  } catch { /* best-effort */ }
  sessionStorage.removeItem('zh:attendance:punch_shown')
  const redirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login'
  try {
    await AuthService.logout({ clearLocalStorage: true, clearSessionStorage: false, clearIndexedDB: false, notifyBackend: true, redirectTo: redirectUrl })
  } catch {
    window.location.href = redirectUrl
  }
}

const Layout: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => localStorage.getItem('zh:sidebar:expanded') === 'true'
  );
  const [showInactivity, setShowInactivity] = useState(false)
  const lastActivityRef = useRef<number>(Date.now())
  // Inactivity auto-logout applies to Sales Representatives only (role 9407).
  const isSalesRep = getCurrentUser()?.role?.role_id === SALES_REP_ROLE_ID

  // ── Inactivity tracking (timestamp-based) ────────────────────────────────
  // Works even when the agent switches to another window/tab or minimizes the
  // browser. Browsers throttle background timers, so we (a) measure real elapsed
  // time via Date.now() instead of trusting tick counts, and (b) re-check the
  // instant the tab regains visibility/focus. An agent who walked away is caught
  // the moment they return — and within ~1 min otherwise, since throttled
  // background timers still fire. Time spent hidden counts as inactivity (no
  // activity events arrive while hidden, so lastActivity stops advancing).
  useEffect(() => {
    if (!isSalesRep) return   // only Sales Representatives are auto-logged-out on inactivity
    const onActivity = () => {
      if (!showInactivity) lastActivityRef.current = Date.now()
    }
    const checkIdle = () => {
      if (showInactivity) return
      if (Date.now() - lastActivityRef.current >= INACTIVITY_MS) setShowInactivity(true)
    }
    const onVisible = () => { if (!document.hidden) checkIdle() }

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    const poll = setInterval(checkIdle, 1000)

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity))
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      clearInterval(poll)
    }
  }, [showInactivity, isSalesRep])

  function handleInactivityDismiss() {
    setShowInactivity(false)
    lastActivityRef.current = Date.now()
  }

  function handleInactivityTimeout() {
    setShowInactivity(false)
    punchOutAndLogout("inactivity")   // punch out (logged as inactivity) first, then logout
  }

  // ── Auto punch-in on load + presence heartbeat ───────────────────────────
  // No modal: logging in IS punching in. ensure-in is idempotent (only punches
  // in if absent/punched-out, never punches out). A 60s heartbeat then keeps
  // last_seen_at fresh; the server punches out agents whose heartbeat goes
  // stale (browser closed) — see Celery rr-heartbeat-timeout.
  useEffect(() => {
    let cancelled = false
    const punchInOnLoad = async () => {
      try {
        const res = await rrEnsurePunchedIn()
        const d = res?.data as { action?: string; at?: string } | null
        if (!cancelled && d?.action === 'punch_in') {
          toast.success(`Punched in at ${d.at}`, { autoClose: 3000 })
        }
      } catch { /* best-effort */ }
    }
    const t = setTimeout(punchInOnLoad, 800)

    // Heartbeat loop
    const beat = () => { rrHeartbeat().catch(() => {}) }
    const hb = setInterval(beat, HEARTBEAT_MS)
    const onVisibleBeat = () => { if (!document.hidden) beat() }
    document.addEventListener('visibilitychange', onVisibleBeat)

    return () => {
      cancelled = true
      clearTimeout(t)
      clearInterval(hb)
      document.removeEventListener('visibilitychange', onVisibleBeat)
    }
  }, [])

  // End-of-shift auto punch-out removed — no shift-window enforcement on the
  // client. Agents are not punched out or notified based on the hour of day.

  useEffect(() => {
    socketService.connect();
    setupNotificationListeners();
    return () => { socketService.disconnect(); };
  }, []);

  const setupNotificationListeners = () => {
    socketService.on('new_lead', (data: any) => {
      toast.success(
        <div>
          <strong>New Lead Assigned!</strong>
          <div className="text-sm mt-1">{data.lead_name} from {data.source || 'Unknown source'}</div>
        </div>,
        {
          autoClose: 5000,
          onClick: () => { window.location.href = `/leads?lead_id=${data.lead_id}`; }
        }
      );
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('New Lead Assigned!', {
          body: `${data.lead_name} from ${data.source || 'Unknown source'}`,
          icon: '/assets/dino.png',
          tag: `lead-${data.lead_id}`,
        });
      }
    });

    socketService.on('reminder_due', (data: any) => {
      toast.warning(
        <div>
          <strong>Reminder Alert!</strong>
          <div className="text-sm mt-1">{data.event_title}</div>
          <div className="text-xs text-gray-600 mt-1">{data.event_date_time}</div>
        </div>,
        {
          autoClose: 8000,
          onClick: () => { window.location.href = '/activities'; }
        }
      );
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Reminder Alert!', {
          body: `${data.event_title} - ${data.event_date_time}`,
          icon: '/assets/dino.png',
          tag: `reminder-${data.event_id}`,
        });
      }
    });

    socketService.on('lead_status_changed', (data: any) => {
      toast.info(`Lead status updated: ${data.lead_name} → ${data.new_status}`);
    });
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <AppSidebar isExpanded={sidebarExpanded} setIsExpanded={setSidebarExpanded} />
      {/* pl-[52px] always reserves space for the collapsed rail; expanded sidebar floats over content */}
      <main
        className="pl-[52px] h-screen overflow-y-auto"
        onClick={() => { if (sidebarExpanded) setSidebarExpanded(false); }}
      >
        <Outlet />
      </main>


      {showInactivity && isSalesRep && (
        <InactivityWarningModal
          durationSeconds={INACTIVITY_COUNTDOWN_SECONDS}
          onDismiss={handleInactivityDismiss}
          onTimeout={handleInactivityTimeout}
        />
      )}
    </div>
  );
};

export default Layout;
